import { useState, useMemo } from 'react';
import {
  List,
  ListSubheader,
  ListItemSecondaryAction,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  ListItemIcon,
  Container,
  AvatarProps,
} from '@mui/material';
import { AlertDialog, AvatarsCell, sortString } from '@centerpiece/components';
import {
  PersonAddOutlined,
  PersonOutlineOutlined,
  CorporateFareOutlined,
  AccountCircleOutlined,
  Edit,
  Delete,
  Search as SearchIcon,
  Add,
} from '@mui/icons-material';
import {
  useGetSubscriptionContactsQuery,
  useDeleteSubscriptionContactMutation,
  useGetOrganizationsQuery,
  organization,
} from 'store/api';
import { ContactTooltip } from './ContactTooltip';
import { ContactComponent } from './ContactComponent';
import { toast } from 'react-toastify';
import { Contact, Organization, SubscriptionContact } from 'store/api/models';
import {
  groupContactsByOrganization,
  groupContactsByRole,
} from 'utils/contacts.utils';

export const ContactList = () => {
  const [view, setView] =
    useState<'contact' | 'organization' | 'role'>('organization');
  const [openAlert, setOpenAlert] = useState<number>();
  const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [hover, setHover] = useState<boolean>(false);
  const handleOpenSpeedDial = () => setOpenSpeedDial(true);
  const handleCloseSpeedDial = () => setOpenSpeedDial(false);
  const [openDialog, setOpenDialog] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    organization?: boolean;
    initialValues?: any;
  }>({ open: false });
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });

  const roles = useMemo(() => {
    return [
      ...new Set<string>(
        subscription_contacts
          .map(sc => sc?.role_r?.name)
          .filter((x: any) => !!x)
      ),
    ].sort();
  }, [subscription_contacts]);
  const [deleteSubscriptionContact] = useDeleteSubscriptionContactMutation();

  const ListOfContacts = ({
    sc,
    index,
  }: {
    sc: SubscriptionContact;
    index: number;
  }) => {
    return (
      <>
        {/* <Divider /> */}
        <ListItem
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() =>
            setOpenDialog({
              open: true,
              id: sc.id,
              disabled: true,
            })
          }
          key={index}
          sx={hover ? { backgroundColor: 'grey' } : {}}
        >
          <ListItemAvatar sx={{ minWidth: 'unset', pr: 2 }}>
            <AvatarsCell
              small={true}
              data={[{ ...sc } as any]}
              tooltip={ContactTooltip}
            />
          </ListItemAvatar>
          <ListItemText
            primary={
              !!sc?.contact_r?.firstname
                ? `${sc?.contact_r?.firstname} ${sc?.contact_r?.lastname}`
                : sc?.contact_r?.email
            }
            secondary={sc?.role_r?.name}
            // secondary={
            //   !!sc?.contact_r?.email ? `  (${sc?.contact_r.email})` : ``
            // }
          />
          <ListItemSecondaryAction>
            <Tooltip title={'Edit'}>
              <IconButton
                onClick={() =>
                  setOpenDialog({
                    open: true,
                    id: sc?.id,
                    // organizations,
                  })
                }
              >
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Delete'}>
              {openAlert && openAlert === sc.id ? (
                <IconButton>
                  <CircularProgress size={22} />
                </IconButton>
              ) : (
                <IconButton onClick={() => setOpenAlert(sc.id)}>
                  <Delete />
                </IconButton>
              )}
            </Tooltip>
          </ListItemSecondaryAction>
        </ListItem>
        <Divider />
      </>
    );
  };
  const ListOfOrganizations = ({
    org,
    index,
  }: {
    org: Organization;
    index: number;
  }) => {
    return (
      <>
        {/* <Divider /> */}
        <ListItem
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() =>
            setOpenDialog({
              open: true,
              id: org.id,
              organization: true,
              disabled: true,
            })
          }
          key={index}
          sx={hover ? { backgroundColor: 'grey' } : {}}
        >
          <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
            <CorporateFareOutlined color="secondary" />
          </ListItemIcon>
          <ListItemText primary={org?.name} secondary={org?.role_r?.name} />
          {org && (
            <ListItemSecondaryAction>
              <Tooltip title={'Edit'}>
                <IconButton
                  onClick={() =>
                    setOpenDialog({
                      open: true,
                      id: org?.id,
                      organization: true,
                      // organizations,
                    })
                  }
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title={'Delete'}>
                {openAlert && openAlert === org.id ? (
                  <IconButton>
                    <CircularProgress size={22} />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => setOpenAlert(org.id)}>
                    <Delete />
                  </IconButton>
                )}
              </Tooltip>
            </ListItemSecondaryAction>
          )}
        </ListItem>
        <Divider />
      </>
    );
  };
  const filteredContacts = [...(subscription_contacts || [])]
    ?.filter(sc => {
      if (view === 'contact') {
        return (
          sc.contact_r?.firstname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.lastname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase())
        );
      } else if (view === 'organization') {
        return (
          sc.contact_r?.firstname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.lastname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          sc.organization_r?.name?.toLowerCase()?.includes(search.toLowerCase())
        );
      } else if (view === 'role') {
        return (
          sc.contact_r?.firstname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.lastname
            ?.toLowerCase()
            ?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          sc.contact_r?.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          sc.role_r?.name?.toLowerCase()?.includes(search.toLowerCase())
        );
      }
    })
    ?.sort((a, b) =>
      sortString(
        view === 'contact'
          ? `${a.contact_r?.firstname} ${a.contact_r?.lastname}`
          : view === 'organization'
          ? a.organization_r?.name || ''
          : a.role_r?.name,
        view === 'contact'
          ? `${b.contact_r?.firstname} ${b.contact_r?.lastname}`
          : view === 'organization' || ''
          ? b.organization_r?.name
          : b.role_r?.name
      )
    );
  const filteredOrgs = groupContactsByOrganization(
    filteredContacts,
    organizations.filter(o =>
      o.name?.toLowerCase()?.includes(search.toLowerCase())
    )
  );
  const filteredRoles = groupContactsByRole(
    filteredContacts,
    organizations.filter(o =>
      o.name?.toLowerCase()?.includes(search.toLowerCase())
    )
  );
  return (
    <>
      <AlertDialog
        open={!!openAlert}
        title={`Delete contact`}
        content={`Are you sure you want to delete this contact?`}
        onClose={() => {
          setOpenAlert(undefined);
        }}
        onConfirm={async () => {
          if (openAlert && openAlert > 0) {
            try {
              await deleteSubscriptionContact(openAlert);
              toast.success('Contact successfully deleted');
            } catch (error) {
              toast.success('Unable to delete contact');
            }
          }
          setOpenAlert(undefined);
        }}
      />
      {openDialog.open ? (
        <ContactComponent
          close={() => setOpenDialog({ open: false })}
          {...openDialog}
        />
      ) : (
        <>
          <Box sx={{ pt: 0, pb: { xs: 3, sm: 1 } }}>
            <Grid
              container
              alignItems={'bottom'}
              justifyContent={'space-between'}
              spacing={0}
            >
              <Grid item>
                <Typography
                  variant="h6"
                  component={'span'}
                  sx={{ color: 'black', pl: 1 }}
                >
                  {view === 'role' ? (
                    <span>
                      Contact List{' '}
                      <small>
                        <i>(Grouped by Role)</i>
                      </small>
                    </span>
                  ) : view === 'organization' ? (
                    <span>
                      Contact List{' '}
                      <small>
                        <i>(Grouped by Organization)</i>
                      </small>
                    </span>
                  ) : (
                    'Contact List'
                  )}
                </Typography>
              </Grid>
              <Grid item>
                <Grid
                  container
                  alignItems={'bottom'}
                  justifyContent={'flex-end'}
                  spacing={2}
                >
                  <Grid item>
                    <ToggleButtonGroup
                      value={view}
                      exclusive
                      onChange={(event: any, value: any) => setView(value)}
                    >
                      {/* <ToggleButton value="contact">
                        <PersonOutlineOutlined />
                      </ToggleButton> */}
                      <ToggleButton value="organization">
                        <CorporateFareOutlined />
                      </ToggleButton>
                      <ToggleButton value="role">
                        <AccountCircleOutlined />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid
                    item
                    // xs={12}
                    // sm={7}
                    // md={9}
                    sx={{ textAlign: 'right' }}
                  >
                    <TextField
                      variant="outlined"
                      fullWidth
                      // size="small"
                      placeholder="Search Contacts"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      onChange={event =>
                        setSearch((event?.target as any)?.value)
                      }
                      sx={{ maxWidth: { xs: 'none', sm: 340 } }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <List sx={{ width: '100%', bgcolor: 'background.paper', pb: 3 }}>
            {view === 'contact' && (
              <>
                {[...Object.keys(filteredOrgs)].sort()?.map((org, oindex) => {
                  const orgObject = organizations.find(o => o.name === org);
                  return (
                    <>
                      <ListItem
                        sx={{
                          backgroundColor: theme => theme.palette.grey[100],
                          fontWeight: '600',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
                          <CorporateFareOutlined color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={org}
                          secondary={orgObject?.role_r?.name}
                        />
                        {orgObject && (
                          <ListItemSecondaryAction>
                            <Tooltip title={'Edit'}>
                              <IconButton
                                onClick={() =>
                                  setOpenDialog({
                                    open: true,
                                    id: orgObject?.id,
                                    organization: true,
                                    // organizations,
                                  })
                                }
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={'Delete'}>
                              {openAlert && openAlert === orgObject.id ? (
                                <IconButton>
                                  <CircularProgress size={22} />
                                </IconButton>
                              ) : (
                                <IconButton
                                  onClick={() => setOpenAlert(orgObject.id)}
                                >
                                  <Delete />
                                </IconButton>
                              )}
                            </Tooltip>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                      {/* <Box sx={{ pl: 2 }}>
                        {filteredOrgs[org]?.map((sc: any, index: number) => (
                          <ListOfContacts sc={sc} index={index} />
                        ))}
                      </Box> */}
                    </>
                  );
                })}
                {filteredContacts?.map((sc, index) => (
                  <ListOfContacts sc={sc} index={index} />
                ))}
              </>
            )}
            {view === 'organization' &&
              [...Object.keys(filteredOrgs)].sort()?.map((org, oindex) => {
                const orgObject = organizations.find(o => o.name === org);
                return (
                  <>
                    <ListItem
                      sx={{
                        backgroundColor: theme => theme.palette.grey[100],
                        fontWeight: '600',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
                        <CorporateFareOutlined color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={org}
                        secondary={orgObject?.role_r?.name}
                      />
                      {orgObject && (
                        <ListItemSecondaryAction>
                          <Tooltip title={'Add Contact'}>
                            <IconButton
                              onClick={() =>
                                setOpenDialog({
                                  open: true,
                                  initialValues: {
                                    organization: orgObject,
                                    role: orgObject.role_r,
                                  },
                                })
                              }
                            >
                              <Add />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={'Edit'}>
                            <IconButton
                              onClick={() =>
                                setOpenDialog({
                                  open: true,
                                  id: orgObject?.id,
                                  organization: true,
                                  // organizations,
                                })
                              }
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={'Delete'}>
                            {openAlert && openAlert === orgObject.id ? (
                              <IconButton>
                                <CircularProgress size={22} />
                              </IconButton>
                            ) : (
                              <IconButton
                                onClick={() => setOpenAlert(orgObject.id)}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Tooltip>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                    <Box sx={{ pl: 2 }}>
                      {filteredOrgs[org]?.map((sc: any, index: number) => (
                        <ListOfContacts sc={sc} index={index} />
                      ))}
                    </Box>
                  </>
                );
              })}
            {view === 'role' &&
              [...Object.keys(filteredRoles)].sort()?.map((role, rindex) => (
                <>
                  <ListItem
                    sx={{
                      backgroundColor: theme => theme.palette.grey[100],
                      fontWeight: '600',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
                      <AccountCircleOutlined color="secondary" />
                    </ListItemIcon>
                    {role}
                  </ListItem>
                  <Box sx={{ pl: 2 }}>
                    {filteredRoles[role]?.organizations?.map(
                      (org: any, index: number) => (
                        <ListOfOrganizations org={org} index={index} />
                      )
                    )}
                    {filteredRoles[role]?.contacts?.map(
                      (sc: any, index: number) => (
                        <ListOfContacts sc={sc} index={index} />
                      )
                    )}
                  </Box>
                </>
              ))}
          </List>
          <SpeedDial
            ariaLabel="SpeedDial basic example"
            sx={{
              position: 'fixed',
              right: 0,
              px: 4,
              width: '100%',
              boxSizing: 'border-box',
              bottom: 64,
              alignItems: 'flex-end',
            }}
            icon={<SpeedDialIcon id="speeddial" />}
            onClose={handleCloseSpeedDial}
            onOpen={handleOpenSpeedDial}
            open={openSpeedDial}
          >
            {false && (
              <SpeedDialAction
                key={'contact'}
                icon={<PersonAddOutlined id={'contact-speeddial'} />}
                tooltipTitle={'Add Contact'}
                tooltipOpen
                onClick={() => setOpenDialog({ open: true })}
              />
            )}
            <SpeedDialAction
              key={'organization'}
              icon={<CorporateFareOutlined id={'organization-speeddial'} />}
              tooltipTitle={'Add Organization'}
              tooltipOpen
              onClick={() => setOpenDialog({ open: true, organization: true })}
            />
          </SpeedDial>
        </>
      )}
    </>
  );
};
